import { IconArrowInbox } from 'central-icons/IconArrowInbox';
import { useEffect, useState } from 'react';

import { FeatureFlag } from '@revelio/core';
import type { UploadFileResult } from '@revelio/data-access';
import { IconButton, Menu, MenuButton, MenuList } from '@revelio/ui';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { usePageExport } from '../Chart/chart-menu/PageExportProvider';
import { useFileName } from '../Chart/utils/useFileName';
import { PdfPreviewModal } from '../Modal/PdfPreviewModal';
import { PptPreviewModal } from '../Modal/PptPreviewModal';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType, RevelioPageId } from '../PageConfigProvider/types';
import {
  ShareReportMenuItem,
  ShareReportModal,
} from '../SharedReport/ShareReportModal';
import { DataExportMenuItem } from './DataExportMenuItem';
import { PdfExportMenuItem } from './PdfExportMenuItem';
import { PostingDiscoveryDataExportMenuItem } from './PostingDiscoveryDataExportMenuItem';
import { PostingDiscoverySelectedDataExportMenuItem } from './PostingDiscoverySelectedDataExportMenuItem';
import { PptExportMenuItem } from './PptExportMenuItem';
import { ScreenerDataExportMenuItem } from './ScreenerDataExportMenuItem';
import { TalentDiscoveryDataExportMenuItem } from './TalentDiscoveryDataExportMenuItem';
import { TalentDiscoverySelectedDataExportMenuItem } from './TalentDiscoverySelectedDataExportMenuItem';
import { useExportCover } from './useExportCover';

export const DownloadMenuButton = () => {
  const { revelioPageId, config: tabContent } = useTabContentConfig();
  const isTalentDiscoveryPage =
    revelioPageId === RevelioPageId.TALENT_DISCOVERY;
  const isPostingsDiscoveryPage =
    revelioPageId === RevelioPageId.POSTING_DISCOVERY;
  const isScreenerPage = isScreenerV2Page(revelioPageId);

  const { config: pageConfig } = usePageConfig();
  const { isPageDataReady, setDefaultExportFileName } = usePageExport();
  const isSharedReportsEnabled = useUnleashFlag(FeatureFlag.SharedReports);

  const exportCover = useExportCover();

  const isReportPage = pageConfig.type === PageType.REPORT;
  const isButtonDisabled = isReportPage && !isPageDataReady;
  const showShareMenuItem = isReportPage && isSharedReportsEnabled;

  // The preview/share modals live HERE, outside the Radix menu content: the
  // menu unmounts its content on close (which a menu-item click triggers), so
  // any modal rendered inside an item would unmount before — or the moment —
  // it opens. Items report up via callbacks instead.
  const [pptPreview, setPptPreview] = useState<UploadFileResult | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [isShareOpen, setIsShareOpen] = useState(false);

  const defaultFileName = useFileName({
    primaryFilterId: tabContent.primaryFilter?.id,
  });
  useEffect(() => {
    setDefaultExportFileName(defaultFileName);
  }, [defaultFileName, setDefaultExportFileName]);

  const handleClosePdfPreview = () => {
    const url = pdfPreviewUrl;
    setPdfPreviewUrl(null);
    // Give the modal's close transition time to finish before the blob the
    // iframe renders is revoked.
    setTimeout(() => {
      if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    }, 300);
  };

  return (
    <>
      <Menu>
        <MenuButton>
          <IconButton
            variant="ghost"
            shape="squared"
            icon={<IconArrowInbox size={16} />}
            aria-label="More actions"
            title={isButtonDisabled ? 'Preparing download...' : 'Download'}
            data-testid="download-menu-button"
            disabled={isButtonDisabled}
          />
        </MenuButton>
        <MenuList align="end">
          {isTalentDiscoveryPage && <TalentDiscoveryDataExportMenuItem />}
          {isTalentDiscoveryPage && (
            <TalentDiscoverySelectedDataExportMenuItem />
          )}
          {isPostingsDiscoveryPage && <PostingDiscoveryDataExportMenuItem />}
          {isPostingsDiscoveryPage && (
            <PostingDiscoverySelectedDataExportMenuItem />
          )}
          {isScreenerPage && <ScreenerDataExportMenuItem />}
          {!isPostingsDiscoveryPage && (
            <PdfExportMenuItem onPreviewReady={setPdfPreviewUrl} />
          )}
          {!isPostingsDiscoveryPage && <DataExportMenuItem />}
          {!isPostingsDiscoveryPage && (
            <PptExportMenuItem onPreviewReady={setPptPreview} />
          )}
          {showShareMenuItem && (
            <ShareReportMenuItem onOpen={() => setIsShareOpen(true)} />
          )}
        </MenuList>
      </Menu>
      <PptPreviewModal
        isOpen={pptPreview !== null}
        onClose={() => setPptPreview(null)}
        title={exportCover.title || defaultFileName || 'PowerPoint file editor'}
        previewUrl={pptPreview?.previewUrl}
        downloadUrl={pptPreview?.downloadUrl}
        editUrl={pptPreview?.editUrl}
      />
      <PdfPreviewModal
        isOpen={pdfPreviewUrl !== null}
        onClose={handleClosePdfPreview}
        title={defaultFileName || 'PDF file preview'}
        previewUrl={pdfPreviewUrl ?? undefined}
      />
      {showShareMenuItem && (
        <ShareReportModal
          isOpen={isShareOpen}
          onClose={() => setIsShareOpen(false)}
        />
      )}
    </>
  );
};
